// File: RecordHourCorrectionPage.js
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";

// Icons
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNoteOutlined";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Custom components
import CustomAlert from "../../components/CustomAlert";

// API service
import recordHourCorrectionApi from "../../api/recordHourCorrectionApi";
import Configs from "../../configs/Configs";

const DEFAULT_AVATAR = "https://www.gravatar.com/avatar/?d=mp&s=80";

/**
 * Allows:
 *  - digits only (e.g. "123")
 *  - optional single leading '-' (e.g. "-123")
 * Rejects all other characters.
 */
const sanitizeSignedInteger = (raw) => {
  if (raw == null) return "";
  let v = String(raw).replace(/[^\d-]/g, "");
  const isNegative = v.startsWith("-");
  const digitsOnly = v.replace(/-/g, "");
  return (isNegative ? "-" : "") + digitsOnly;
};

const toISODate = (d) => {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

function RecordHourCorrectionPage() {
  const { state } = useLocation();

  const user_id = state?.user_id;
  const photo = state?.photo;

  const now = new Date();

  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const [selectedDate, setSelectedDate] = useState(toISODate(now));

  const [hoursError, setHoursError] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [dateError, setDateError] = useState("");

  const [loading, setLoading] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const showAlert = (msg, severity = "info") => {
    setAlertMessage(msg);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  useEffect(() => {
    if (!user_id) {
      showAlert("No user selected. Go back and select a user.", "error");
    }
  }, [user_id]);

  const validate = () => {
    let valid = true;

    if (!hours || hours === "-" || !/^-?\d+$/.test(hours) || Number(hours) === 0) {
      setHoursError("Hours must be a non-zero number");
      valid = false;
    } else {
      setHoursError("");
    }

    if (!reason.trim()) {
      setReasonError("Reason is required");
      valid = false;
    } else {
      setReasonError("");
    }

    if (!selectedDate) {
      setDateError("Date is required");
      valid = false;
    } else {
      setDateError("");
    }

    return valid;
  };

  const handleSave = async () => {
    if (!user_id) return;
    if (!validate()) return;

    setLoading(true);

    try {
      const [yearStr, monthStr, dayStr] = selectedDate.split("-");

      const payload = {
        user_id,
        hours: Number(hours),
        reason: reason.trim(),
        day: Number(dayStr),
        month: Number(monthStr),
        year: Number(yearStr),
      };

      const res = await recordHourCorrectionApi(payload);

      if (res?.status === "success") {
        showAlert("Hour correction recorded successfully!", "success");
        setHours("");
        setReason("");
      } else {
        showAlert(res?.message || "Failed to record hour correction.", "error");
      }
    } catch (err) {
      console.error(err);
      showAlert("Server error. Could not save hour correction.", "error");
    } finally {
      setLoading(false);
    }
  };

  const inputSx = { "& .MuiInputBase-root": { minHeight: 56 } };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox sx={{ maxWidth: "600px", margin: "0 auto 0 0" }}>
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
              <Avatar
                src={photo ? `${Configs.baseUrl}${photo}` : DEFAULT_AVATAR}
                sx={{ width: 80, height: 80 }}
              />
              <MDBox flexGrow={1} minWidth={200}>
                <MDTypography variant="h5" fontWeight="bold" mb={1}>
                  Hour Correction for {state?.full_name || "User"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Enter manual hour correction details for the selected user
                </MDTypography>
              </MDBox>
            </MDBox>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: "lg" }}>
            <MDBox mb={2}>
              <Grid container spacing={3}>
                {/* Hours */}
                <Grid item xs={12}>
                  <TextField
                    label={
                      <MDBox display="flex" alignItems="center">
                        <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                        Hours (positive or negative)
                      </MDBox>
                    }
                    fullWidth
                    type="text"
                    value={hours}
                    onChange={(e) => {
                      const sanitized = sanitizeSignedInteger(e.target.value).slice(0, 3);
                      setHours(sanitized);
                    }}
                    error={Boolean(hoursError)}
                    helperText={hoursError}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "-?[0-9]*",
                      maxLength: 3,
                    }}
                    InputProps={{
                      sx: {
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: hoursError ? "red" : undefined,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: hoursError ? "red" : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Reason */}
                <Grid item xs={12}>
                  <TextField
                    label={
                      <MDBox display="flex" alignItems="center">
                        <EventNoteIcon fontSize="small" sx={{ mr: 1 }} />
                        Reason (Required)
                      </MDBox>
                    }
                    fullWidth
                    multiline
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    error={Boolean(reasonError)}
                    helperText={reasonError}
                    InputProps={{
                      sx: {
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: reasonError ? "red" : undefined,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: reasonError ? "red" : undefined,
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Date Picker */}
                <Grid item xs={12}>
                  <TextField
                    type="date"
                    label="Correction Date"
                    fullWidth
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    error={Boolean(dateError)}
                    helperText={dateError}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </MDBox>

            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={loading}
              onClick={handleSave}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Record Hour Correction"}
            </MDButton>
          </Paper>
        </MDBox>
      </MDBox>

      <CustomAlert
        message={alertMessage}
        severity={alertSeverity}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default RecordHourCorrectionPage;
